import React, { useCallback, useEffect, useState } from "react";
import { doNothing, classNames } from "@greeter/util";
import { Millis, TimeOfDay } from "@greeter/date";

import { BookingInfo } from "@greeter-venue/components/BookingInfo";
import { BookingListItemHeader } from "@greeter-venue/components/BookingListItemHeader/BookingListItemHeader";
import { BookingListItemContent } from "@greeter-venue/components/BookingListItemContent/BookingListItemContent";
import { Booking, Customer, PhoneNumber } from "@greeter/core";
import { ConfirmSwiper } from "../ConfirmSwiper";
import { ConfirmStage } from "@greeter-venue/components/ConfirmSwiper/ConfirmSwiper";
import ArrivedSwiper from "@greeter-venue/components/ArrivedSwiper";
import { ArrivedStage } from "@greeter-venue/components/ArrivedSwiper/ArrivedSwiper";
import {
  GreenLabel,
  GreyLabel,
  Hint,
  Label,
  useToggle,
  YellowLabel,
} from "@greeter/matter";
import { BookingListItemLabelHeader } from "@greeter-venue/components/BookingListItemLabelHeader";
import { UnconfirmedStage } from "../ConfirmSwiper/UnconfirmedStage";
import { match } from "ts-pattern";
import { LoadingStage } from "../ConfirmSwiper/LoadingStage";
import { DoneStage } from "../ConfirmSwiper/DoneStage";
import { formatDistance } from "date-fns";
import { da } from "date-fns/locale";
import { IconEdit } from "@tabler/icons-react";
import { Routes } from "@greeter-venue/utility/Routes";
import { useHistory } from "react-router-dom";

import css from "./BookingListItem.module.scss";

type OnBooking = (booking: Booking) => void | Promise<void>;

export type BookingListItemProps = {
  booking: Booking;
  customer?: Customer;
  onAccept?: OnBooking;
  onReject?: OnBooking;
  onMarkAsArrived?: OnBooking;
  onEnd?: OnBooking;
};

export function BookingListItem({
  booking,
  customer,
  onAccept = doNothing,
  onReject = doNothing,
  onMarkAsArrived = doNothing,
  onEnd = doNothing,
}: BookingListItemProps) {
  const [acceptStage, setAcceptStage] = useState<ConfirmStage>("unconfirmed");
  const [arrivedStage, setArrivedStage] = useState<ArrivedStage>("unconfirmed");
  const [endStage, setEndStage] = useState<"unconfirmed" | "loading" | "done">(
    "unconfirmed"
  );

  const [isOpen, toggleIsOpen] = useToggle(false);

  const [now, setNow] = useState<Date>(new Date());

  const { groupSize } = booking;

  const timeOfDay = TimeOfDay.fromDate(booking.period.from);

  useEffect(() => {
    const id = setInterval(() => {
      setNow(new Date());
    }, Millis.fromSeconds(5));

    return () => clearInterval(id);
  }, []);

  const history = useHistory();

  const overdueText = `+ ${formatDistance(now, booking.period.from, {
    locale: da,
  })}`;

  return (
    <div
      key={booking.id}
      className={
        !isOpen && Booking.is(booking, "pending")
          ? classNames(css.Card, css.Pending).className
          : css.Card
      }
    >
      {!isOpen && Booking.is(booking, "pending") ? (
        <GreenLabel
          {...classNames(css.Label, css.GreenLabel)}
          text="Ny booking! 🥳"
        />
      ) : !isOpen && Booking.is(booking, "inProgress") ? (
        <GreenLabel
          {...classNames(css.Label)}
          text={booking.createdBy === "venueAdmin" ? "Betalt" : "Ankommet"}
        />
      ) : !isOpen && Booking.is(booking, "accepted") ? (
        <Label
          {...classNames(css.Label, css.WhiteLabel)}
          text={
            booking.createdBy === "venueAdmin" ? "Mangler betaling" : "Godkendt"
          }
        />
      ) : !isOpen &&
        (Booking.is(booking, "overdue") || Booking.is(booking, "missed")) ? (
        <YellowLabel
          {...classNames(css.Label, css.YellowLabel)}
          text={overdueText}
        />
      ) : !isOpen && Booking.is(booking, "rejected") ? (
        <Label {...classNames(css.Label, css.RedLabel)} text="Afvist" />
      ) : !isOpen && Booking.is(booking, "refunded") ? (
        <GreyLabel {...classNames(css.Label)} text="Refunderet" />
      ) : !isOpen && Booking.is(booking, "completed") ? (
        <GreyLabel {...classNames(css.Label)} text="Afsluttet" />
      ) : !isOpen && Booking.is(booking, "created") ? (
        <GreyLabel {...classNames(css.Label)} text="Afventer betaling" />
      ) : (
        !isOpen && <GreyLabel className={css.Label} text={booking.status} />
      )}

      <div onClick={toggleIsOpen} {...classNames(css.BookingListItem)}>
        <div className={css.Wrapper}>
          {!isOpen && customer && (
            <>
              <BookingListItemHeader
                toggled={isOpen}
                arrivalTime={timeOfDay}
                tables={booking.tables}
                partySize={`${groupSize}`}
                tablePrefixClassName={
                  !isOpen && Booking.is(booking, "created")
                    ? css.PendingPrefix
                    : ""
                }
                groupSizePrefixClassName={
                  !isOpen && Booking.is(booking, "created")
                    ? css.PendingPrefix
                    : ""
                }
              />
              <BookingListItemContent
                arrivalDate={booking.period.from}
                name={customer!.name}
                bookingId={booking.id}
              />
            </>
          )}
        </div>
        {isOpen && (
          <div
            {...classNames(
              css.DetailedContent,
              !isOpen ? css.HiddenDetailedContent : ""
            )}
          >
            <BookingListItemLabelHeader
              overdueText={overdueText}
              className={css.BookingListItemLabelHeader}
              createdBy={booking.createdBy}
              name={customer?.name ?? "Kunne ikke hente navn..."}
              bookingStatus={Booking.getStatus(booking)}
              phoneNumber={PhoneNumber.toString(
                customer?.phoneNumber
                  ? customer?.phoneNumber
                  : PhoneNumber.create()
              )}
            />
            {booking && <BookingInfo booking={booking} />}
            <div>
              {booking.status === "pending" && booking.order ? (
                <>
                  <ConfirmSwiper
                    stage={acceptStage}
                    onAccept={async () => {
                      try {
                        setAcceptStage("loading");
                        await onAccept(booking);
                        setAcceptStage("done");
                      } catch {
                        setAcceptStage("unconfirmed");
                      }
                    }}
                  />
                  <Hint text="Når du godkender, trækker du beløbet" />
                </>
              ) : Booking.is(booking, "inProgress") ? (
                match(endStage)
                  .with("unconfirmed", () => (
                    <>
                      <UnconfirmedStage
                        text="Afslut"
                        onAccept={async () => {
                          setEndStage("loading");
                          await onEnd(booking);
                          setEndStage("done");
                          setTimeout(() => setEndStage("unconfirmed"), 5000);
                        }}
                      />
                      <Hint text="Frigør bordet igen så andre kan booke" />
                    </>
                  ))
                  .with("loading", () => <LoadingStage />)
                  .with("done", () => <DoneStage text="Booking afsluttet" />)
                  .exhaustive()
              ) : Booking.is(booking, "accepted") ||
                Booking.is(booking, "overdue") ? (
                <ArrivedSwiper
                  stage={arrivedStage}
                  text={
                    booking.createdBy === "venueAdmin" ? "Betalt" : "Ankommet"
                  }
                  onMarkAsArrived={async () => {
                    setArrivedStage("loading");
                    await onMarkAsArrived(booking);
                    setArrivedStage("done");
                  }}
                />
              ) : (
                Booking.is(booking, "created") &&
                booking.order &&
                booking.order.status === "created" && (
                  <div style={{ fontSize: "0.8rem" }}>
                    Ordren er ikke gået igennem. Du kan enten vente eller afvise
                    og frigøre bordet igen.
                  </div>
                )
              )}
            </div>
            <div className={css.BottomButtonBar}>
              <div />
              {booking.status === "created" || booking.status === "pending" ? (
                <button
                  className={css.TransparentRejectButton}
                  onClick={(event) => {
                    event.stopPropagation();
                    onReject(booking);
                  }}
                  style={{ textAlign: "right" }}
                >
                  Afvis bestilling
                </button>
              ) : (
                <div />
              )}
            </div>
          </div>
        )}
      </div>
      {(!Booking.is(booking, "completed") ||
        !Booking.is(booking, "rejected")) && (
        <div className={css.EditBar}>
          <span className={css.EditBarHint}>
            {booking.createdBy === "venueAdmin" && "Lavet af venue"}
          </span>
          <div
            onClick={() =>
              history.push(
                Routes.mainGroup.editBooking.route({
                  mode: "edit",
                  bookingId: booking.id,
                })
              )
            }
          >
            <IconEdit />
          </div>
        </div>
      )}
    </div>
  );
}
